import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import "../../assets/scss/tables.scss";

/* Kept in step with the server: employees.ctrl.js accepts these types and
   caps the file at 2MB. */
export const PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
export const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

const readableSize = (bytes) =>
    bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const ProfilePhotoModal = ({ show, currentUrl, onClose, onUpload, onRemove }) => {

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    /* start clean each time it opens, and release the preview when it closes */
    useEffect(() => {
        if (show) return undefined;
        setFile(null);
        setError("");
        setBusy(false);
        return undefined;
    }, [show]);

    useEffect(() => {
        if (!file) { setPreview(""); return undefined; }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const choose = (picked) => {
        if (!picked) return;

        if (!PHOTO_TYPES.includes(String(picked.type).toLowerCase())) {
            setError("That file type is not supported. Choose a JPG, PNG or GIF.");
            setFile(null);
            return;
        }

        if (picked.size > PHOTO_MAX_BYTES) {
            setError(`That image is ${readableSize(picked.size)}. The limit is 2MB.`);
            setFile(null);
            return;
        }

        setError("");
        setFile(picked);
    };

    const submit = async () => {
        if (!file || busy) return;
        setBusy(true);
        const ok = await onUpload(file);
        setBusy(false);
        if (ok) onClose();
    };

    const drop = (event) => {
        event.preventDefault();
        choose(event.dataTransfer?.files?.[0]);
    };

    return (
        <Modal show={show} onHide={onClose} backdrop="static" className="wh-modal">
            <Modal.Header closeButton>
                <Modal.Title>Profile Picture</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <label className="wh-upload wh-photo-drop"
                    htmlFor="profile_photo"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={drop}>

                    {preview
                        ? <img className="wh-photo-preview" src={preview} alt="Selected profile" />
                        : currentUrl
                            ? <img className="wh-photo-preview" src={currentUrl} alt="Current profile" />
                            : <CloudUploadOutlinedIcon />}

                    <h6>{file ? file.name : "Choose a picture"}</h6>
                    <p>{file
                        ? `${readableSize(file.size)} — looks good`
                        : "Drag one here, or click to browse"}</p>

                    <input
                        ref={inputRef}
                        id="profile_photo"
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif"
                        onChange={(e) => choose(e.target.files?.[0])}
                    />
                </label>

                {error && <p className="wh-photo-error">{error}</p>}

                <h6 className="wh-section-title">Before you upload</h6>
                <ul className="wh-photo-rules">
                    <li><CheckCircleOutlineRoundedIcon />JPG, JPEG, PNG or GIF</li>
                    <li><CheckCircleOutlineRoundedIcon />2MB or smaller</li>
                    <li><CheckCircleOutlineRoundedIcon />Square images look best — it is shown as a circle</li>
                    <li><CheckCircleOutlineRoundedIcon />Use a clear photo of your face, on a plain background</li>
                </ul>
            </Modal.Body>

            <Modal.Footer>
                {currentUrl && (
                    <Button variant="outlined" className="wh-danger-btn wh-photo-remove"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        disabled={busy}
                        onClick={async () => { await onRemove(); onClose(); }}>
                        Remove
                    </Button>
                )}
                <Button variant="contained" color="inherit" onClick={onClose} disabled={busy}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={submit} disabled={!file || busy}>
                    {busy ? "Uploading…" : "Save Picture"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProfilePhotoModal;
